// Mirrors uzai-backend/src/main/models/crawler/*.ts.
// ObjectIds and dates are exposed as strings on the wire.

import { Integration } from './integration';

// ── Constants ────────────────────────────────────────────────────────────────

export const MAX_DISCOVERY_DEPTH = 3;
// Bounds of a discovery's page cap, any value in between (a small site, a sample).
export const MAX_PAGES_OPTIONS_MIN = 10;
export const MAX_PAGES_OPTIONS_MAX = 10_000;
export const MAX_PAGES_DEFAULT = 500;

export const CrawlerRecurrenceValues = ['month', 'week', 'once'] as const;
export type CrawlerRecurrence = typeof CrawlerRecurrenceValues[number];

export const CRAWLER_DISCOVERY_STRATEGY_TYPES = ['GENERIC', 'AMAZON'] as const;
export type CrawlerDiscoveryStrategyType = typeof CRAWLER_DISCOVERY_STRATEGY_TYPES[number];

export enum CrawlerType {
  SINGLE_URL = 'SINGLE_URL',
  EXCEL_BATCH = 'EXCEL_BATCH',
  DISCOVERY = 'DISCOVERY',
}

export enum CrawlerStatus {
  PENDING = 'PENDING',
  CRAWLING = 'CRAWLING',
  UPLOADING = 'UPLOADING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  PARTIALLY_FAILED = 'PARTIALLY_FAILED',
  DISCOVERING = 'DISCOVERING',
  CANCELED = 'CANCELED',
}

// Statuses during which the crawler is queued or actively running (never settled)
export const ACTIVE_CRAWLER_STATUSES: ReadonlySet<CrawlerStatus> = new Set([
  CrawlerStatus.PENDING,
  CrawlerStatus.CRAWLING,
  CrawlerStatus.UPLOADING,
  CrawlerStatus.DISCOVERING,
]);

// ── Sub-shapes ───────────────────────────────────────────────────────────────

export interface CrawlerConfig {
  screenshotFormat: 'jpeg' | 'png';
  quality?: number;
  viewport: {
    width: number;
    height: number;
  };
  concurrency: number;
  delay?: number;
  batchSize: number;
  pageTimeout?: number;
  headers?: Record<string, string>;
}

export interface CrawlerProgress {
  total: number;
  processed: number;
  limitReached?: boolean;
  failed: number;
  currentBatch?: number;
  totalBatches?: number;
  status: CrawlerStatus;
  startedAt?: string;
  completedAt?: string;
  errorMessage?: string;
}

export interface CrawlerOutput {
  zipBlobUrl?: string;
  manifestBlobUrl?: string;
  screenshotBlobUrls?: string[];
  totalSize?: number;
}

export interface CrawlerTrademark {
  ref: string;
  name?: string;
  identifierNumber: string;
  countryOfDesignation: string;
}

// ── Per-type specs ───────────────────────────────────────────────────────────

export interface SingleUrlCrawlerSpec {
  url: string;
}

export interface ExcelBatchCrawlerSpec {
  excelBlobUrl: string;
  excelFileName: string;
  urlColumn: string;
  output?: CrawlerOutput;
}

export interface DiscoveryCrawlerSpec {
  url: string;
  discoveryDepth: number;
  strategy: CrawlerDiscoveryStrategyType;
  pendingJobs?: number;
  maxDiscoverablePages?: number;
}

// ── Discriminated Crawler union ──────────────────────────────────────────────

interface CrawlerBase {
  _id: string;
  integrationId: string;
  trademarks: CrawlerTrademark[];
  recurrence: CrawlerRecurrence;
  config: CrawlerConfig;
  progress: CrawlerProgress;
  lastExecutionId?: string;
  executionCount: number;
  // Backend-computed status/retry flags, present on list/search responses.
  eligibility?: CrawlerStatusFlags;
  // Selected country/territory codes (shared across types; only DISCOVERY uses them).
  countryCodes?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface SingleUrlCrawler extends CrawlerBase {
  type: CrawlerType.SINGLE_URL;
  spec: SingleUrlCrawlerSpec;
}

export interface ExcelBatchCrawler extends CrawlerBase {
  type: CrawlerType.EXCEL_BATCH;
  spec: ExcelBatchCrawlerSpec;
}

export interface DiscoveryCrawler extends CrawlerBase {
  type: CrawlerType.DISCOVERY;
  spec: DiscoveryCrawlerSpec;
}

export type Crawler = SingleUrlCrawler | ExcelBatchCrawler | DiscoveryCrawler;

// ── Combined search/detail shape returned by the backend ─────────────────────

export interface IntegrationWithCrawler {
  integration: Integration;
  crawler: Crawler;
}

export enum CrawlerLaunchBlockReason {
  DISCOVERY_IN_PROGRESS = 'DISCOVERY_IN_PROGRESS',
  NO_DISCOVERED_URLS = 'NO_DISCOVERED_URLS',
  NO_SELECTED_URLS = 'NO_SELECTED_URLS',
  URLS_ALREADY_DISCOVERED = 'URLS_ALREADY_DISCOVERED',
  CRAWL_ALREADY_RUN = 'CRAWL_ALREADY_RUN',
}

/**
 * DB-free status/retry flags (mirror of the backend CrawlerStatusFlags).
 * Embedded in each search row so the list page consumes backend-computed
 * state instead of re-deriving the predicates on the client.
 */
export interface CrawlerStatusFlags {
  isDiscoveryRunning: boolean;
  isCrawlRunning: boolean;
  hasFailedUrls: boolean;
  canRetryFailedUrls: boolean;
}

/**
 * Launch eligibility + live status for a crawler (mirror of the backend DTO).
 * Computed once per read; the source of truth for the wizard's action buttons.
 */
export interface CrawlerLaunchEligibility {
  status: CrawlerStatus;
  canRunDiscovery: boolean;
  canRunCrawl: boolean;
  discoveryBlockReason?: CrawlerLaunchBlockReason;
  crawlBlockReason?: CrawlerLaunchBlockReason;
  isDiscoveryRunning: boolean;
  isCrawlRunning: boolean;
  runningExecutionId?: string;
  hasFailedUrls: boolean;
  canRetryFailedUrls: boolean;
  discoveryGate: boolean;
  crawlGate: boolean;
  // discovery integrations: pages the crawl will capture
  capturableCount?: number;
}
